import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, HStack, Input, Select, Text, VStack } from '@chakra-ui/react';
import { TUserDemographicsInfo } from '@/types/User.types';
import RadioButtonGroup from '@/components/molecules/RadioButtonGroup';
import {
  ETHNICITIES,
  LEARNING_STYLES,
  LEVELS_OF_EDUCATION,
  OCCUPATIONS,
  OPTION_OTHER,
  RELATIONSHIP_STATUSES,
  SEX_OPTIONS,
} from '@/utils/demographicDataMappers';
import CheckBoxTile from '@/components/atoms/CheckBoxTile';
import { useForm } from 'react-hook-form';
import useUSStates from '@/hooks/meta/useUSStates';
import colors from '@/theme/colors';
import { LOCATION_STATE_DEFAULT_VALUE } from '@/constants';
import { isAfter, isBefore, isValid } from 'date-fns';

type Props = {
  submitLabel?: string;
  onboarding?: boolean;
  onSkip?: () => void;
  onSubmit?: (data: TUserDemographicsInfo) => void;
  isLoading?: boolean;
  defaultFormValues: TUserDemographicsInfo | null;
};

const LABEL_INPUT_VERTICAL_GAP = '20px';

const eightTeenYearsAgoTimeStamp = new Date().setFullYear(new Date().getFullYear() - 18);
const eightTeenYearsAgo = new Date(eightTeenYearsAgoTimeStamp).toISOString().split('T')[0];

const oneHundredsYearsAgoTimeStamp = new Date().setFullYear(new Date().getFullYear() - 100);
const oneHundredsYearsAgo = new Date(oneHundredsYearsAgoTimeStamp).toISOString().split('T')[0];

const DemographicsForm = ({ defaultFormValues, onSubmit, isLoading = false, submitLabel = 'Continue', onboarding = false, onSkip }: Props) => {
  const { data: USStates } = useUSStates();

  const { register, setValue, watch, formState, getValues, setFocus } = useForm<TUserDemographicsInfo>({
    defaultValues: { ...defaultFormValues },
    mode: 'all',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (defaultFormValues) {
      setFocus('dateOfBirth');
    }
  }, [defaultFormValues]);

  const { isValid: isValidInputFields, defaultValues, errors, touchedFields } = formState;

  const [otherSex, setOtherSex] = useState(defaultValues?.sex === OPTION_OTHER ? defaultFormValues?.sexOther : '');
  const selectedSex = watch('sex');

  const locationStateValue = watch('state');

  const [otherOccupation, setOtherOccupation] = useState(
    defaultValues?.occupation === OPTION_OTHER ? defaultFormValues?.occupationOther : '',
  );
  const selectedOccupation = watch('occupation');

  const [otherEthnicity, setOtherEthnicity] = useState(
    defaultValues?.ethnicity === OPTION_OTHER ? defaultFormValues?.ethnicityOther : '',
  );
  const selectedEthnicity = watch('ethnicity');

  const [otherRelationshipStatus, setOtherRelationshipStatus] = useState(
    defaultValues?.relationshipStatus === OPTION_OTHER ? defaultFormValues?.relationshipStatusOther : '',
  );
  const selectedRelationshipStatus = watch('relationshipStatus');

  const [otherLevelOfEducation, setOtherLevelOfEducation] = useState(
    defaultValues?.education === OPTION_OTHER ? defaultFormValues?.educationOther : '',
  );
  const selectedLevelOfEducation = watch('education');

  const selectedLearningStylePreference = watch('learningStylePreference');

  const updateLearningStyles = useCallback(
    ({ label }: { label: string }) => {
      if (selectedLearningStylePreference?.includes(label)) {
        const newLearningStylePreference = selectedLearningStylePreference.filter(
          (learningStyle) => learningStyle !== label,
        );
        setValue('learningStylePreference', newLearningStylePreference);
      } else {
        setValue('learningStylePreference', [...(selectedLearningStylePreference || []), label]);
      }
    },
    [selectedLearningStylePreference, setValue],
  );

  const getFormDataWithOtherOptions = useCallback((): TUserDemographicsInfo => {
    const allValues: TUserDemographicsInfo = getValues();

    if (allValues.sex === OPTION_OTHER) {
      allValues.sexOther = otherSex;
    }
    if (allValues.occupation === OPTION_OTHER) {
      allValues.occupationOther = otherOccupation;
    }
    if (allValues.ethnicity === OPTION_OTHER) {
      allValues.ethnicityOther = otherEthnicity;
    }
    if (allValues.relationshipStatus === OPTION_OTHER) {
      allValues.relationshipStatusOther = otherRelationshipStatus;
    }
    if (allValues.education === OPTION_OTHER) {
      allValues.educationOther = otherLevelOfEducation;
    }

    return { ...defaultFormValues, ...allValues };
  }, [
    getValues,
    otherSex,
    otherOccupation,
    otherEthnicity,
    otherRelationshipStatus,
    otherLevelOfEducation,
    defaultFormValues,
  ]);

  const isFormValid = useMemo(() => {
    if (!selectedSex || (selectedSex === OPTION_OTHER && !otherSex)) {
      return false;
    }
    if (!onboarding && selectedOccupation === OPTION_OTHER && !otherOccupation) {
      return false;
    }
    if (!selectedEthnicity || (selectedEthnicity === OPTION_OTHER && !otherEthnicity)) {
      return false;
    }
    if (!selectedRelationshipStatus || (selectedRelationshipStatus === OPTION_OTHER && !otherRelationshipStatus)) {
      return false;
    }
    if (!selectedLevelOfEducation || (selectedLevelOfEducation === OPTION_OTHER && !otherLevelOfEducation)) {
      return false;
    }

    if (!selectedLearningStylePreference?.length) return false;

    return isValidInputFields;
  }, [
    selectedSex,
    onboarding,
    selectedOccupation,
    selectedEthnicity,
    selectedRelationshipStatus,
    selectedLevelOfEducation,
    otherSex,
    otherOccupation,
    otherEthnicity,
    otherRelationshipStatus,
    otherLevelOfEducation,
    selectedLearningStylePreference,
    isValidInputFields,
  ]);

  const onFormSubmit = () => {
    const formData = getFormDataWithOtherOptions();
    if (onSubmit) {
      onSubmit(formData);
    }
  };

  const customDateValidation = (value: string) => {
    const newDate = new Date(value);
    const isNewDateValid =
      isValid(newDate) && isAfter(newDate, oneHundredsYearsAgo) && isBefore(newDate, eightTeenYearsAgo);
    return isNewDateValid;
  };

  return (
    <VStack spacing={'48px'}>
      {/* Date of birth */}
      <VStack alignItems={'center'} width={'100%'}>
        <HStack spacing={'20px'} minWidth={'100%'} justifyContent={onboarding ? 'center' : undefined}>
          <VStack flex={onboarding ? undefined : 1} width={onboarding ? '100%' : undefined} maxWidth={onboarding ? '340px' : undefined} alignItems={onboarding ? 'center' : 'flex-start'} gap={LABEL_INPUT_VERTICAL_GAP}>
            <Text variant={'urbanistBoldLarge'}>Date of Birth</Text>
            <Input
              type="date"
              placeholder="Date of Birth"
              className="custom-input"
              isInvalid={!!touchedFields.dateOfBirth && !!errors?.dateOfBirth}
              _placeholder={{ color: 'text.mediumGray', fontWeight: '600', fontSize: 12 }}
              color={'text.darkGray'}
              max={eightTeenYearsAgo}
              min={oneHundredsYearsAgo}
              {...register('dateOfBirth', { required: true, validate: customDateValidation })}
              data-state={touchedFields.dateOfBirth ? (errors.dateOfBirth?.message ? 'invalid' : 'valid') : ''}
            />
          </VStack>
        </HStack>
        <Text variant={'error'}>
          {touchedFields.dateOfBirth && errors?.dateOfBirth ? 'Invalid Date of Birth' : ''}
        </Text>
      </VStack>

      <VStack gap={LABEL_INPUT_VERTICAL_GAP}>
        <Text variant={'urbanistBoldLarge'}>Sex</Text>
        <RadioButtonGroup
          items={SEX_OPTIONS}
          selectedItem={selectedSex}
          onClick={(sex: string) => setValue('sex', sex)}
          textInputValue={otherSex}
          setTextInputValue={setOtherSex}
        />
      </VStack>
      {/* Location */}
      <VStack alignItems={'center'} width={'100%'}>
        <HStack spacing={'20px'} minWidth={'100%'}>
          <VStack flex={1} alignItems={'flex-start'} gap={LABEL_INPUT_VERTICAL_GAP}>
            <Text variant={'urbanistBoldLarge'}>Location</Text>
            <HStack>
              <Input
                placeholder="City"
                className="custom-input"
                _placeholder={{ color: 'text.mediumGray', fontWeight: '600', fontSize: 12 }}
                color={'text.darkGray'}
                isInvalid={!!errors?.city}
                {...register('city', {
                  required: { message: 'City is required', value: true },
                  minLength: { value: 2, message: 'City must have at least 2 characters' },
                  pattern: { value: /^[a-zA-Z\s.]*$/, message: 'City can only contain letters, spaces, and dots.' },
                })}
                data-state={touchedFields.city ? (errors.city ? 'invalid' : 'valid') : ''}
              />

              <Input
                placeholder="ZIP Code"
                className="custom-input"
                _placeholder={{ color: 'text.mediumGray', fontWeight: '600', fontSize: 13 }}
                color={'text.darkGray'}
                isInvalid={!!errors?.zip}
                {...register('zip', {
                  required: { message: 'ZIP Code is required', value: true },
                  pattern: { value: /^\d{5}(-\d{4})?(?!-)$/, message: 'Invalid ZIP Code' },
                })}
                minWidth={'70px'}
                data-state={touchedFields.zip ? (errors.zip ? 'invalid' : 'valid') : ''}
              />
            </HStack>
          </VStack>
          <HStack flex={1} spacing={'10px'}>
            <VStack alignItems={'flex-start'} flex={1} gap={LABEL_INPUT_VERTICAL_GAP}>
              <Text variant={'urbanistBoldLarge'} visibility={'hidden'}>
                _
              </Text>
              <HStack minWidth={'100%'} width={'100%'}>
                <Select
                  className="custom-input custom-select-padding"
                  defaultValue={LOCATION_STATE_DEFAULT_VALUE}
                  isInvalid={touchedFields.state && locationStateValue === LOCATION_STATE_DEFAULT_VALUE}
                  style={
                    locationStateValue === LOCATION_STATE_DEFAULT_VALUE
                      ? {
                          color: colors.text.mediumGray,
                          fontSize: '14px',
                          borderColor: touchedFields.state ? colors.extra.lightRed : '#e2e8f0',
                        }
                      : {}
                  }
                  {...register('state', {
                    required: { message: 'State is required', value: true },
                    validate: (value: string) => (value === LOCATION_STATE_DEFAULT_VALUE ? 'State is required' : true),
                  })}
                  data-state={
                    touchedFields.state
                      ? locationStateValue === LOCATION_STATE_DEFAULT_VALUE
                        ? 'invalid'
                        : 'valid'
                      : ''
                  }
                >
                  <option disabled>{LOCATION_STATE_DEFAULT_VALUE}</option>
                  {(USStates ?? []).map((USState) => (
                    <option key={USState.label} value={USState.code}>
                      {USState.label}
                    </option>
                  ))}
                </Select>
              </HStack>
            </VStack>
          </HStack>
        </HStack>
        <Text variant={'error'}>{errors?.city?.message || errors?.zip?.message || errors?.state?.message || ''}</Text>
      </VStack>

      {!onboarding && <VStack gap={LABEL_INPUT_VERTICAL_GAP}>
        <Text variant={'urbanistBoldLarge'}>Occupation (optional)</Text>
        <RadioButtonGroup
          items={OCCUPATIONS}
          separatorLineAfterIndex={3}
          selectedItem={selectedOccupation}
          onClick={(occupation: string) => setValue('occupation', occupation)}
          textInputValue={otherOccupation}
          setTextInputValue={setOtherOccupation}
        />
      </VStack>}
      <VStack gap={LABEL_INPUT_VERTICAL_GAP}>
        <Text variant={'urbanistBoldLarge'}>Ethnicity</Text>
        <RadioButtonGroup
          items={ETHNICITIES}
          selectedItem={selectedEthnicity}
          onClick={(ethnicity: string) => setValue('ethnicity', ethnicity)}
          textInputValue={otherEthnicity}
          setTextInputValue={setOtherEthnicity}
        />
      </VStack>
      <VStack gap={LABEL_INPUT_VERTICAL_GAP}>
        <Text variant={'urbanistBoldLarge'}>Relationship Status</Text>
        <RadioButtonGroup
          items={RELATIONSHIP_STATUSES}
          selectedItem={selectedRelationshipStatus}
          onClick={(relationshipStatus: string) => setValue('relationshipStatus', relationshipStatus)}
          textInputValue={otherRelationshipStatus}
          setTextInputValue={setOtherRelationshipStatus}
        />
      </VStack>
      <VStack gap={LABEL_INPUT_VERTICAL_GAP}>
        <Text variant={'urbanistBoldLarge'}>Highest level of education you have completed</Text>
        <RadioButtonGroup
          items={LEVELS_OF_EDUCATION}
          selectedItem={selectedLevelOfEducation}
          noOfLines={2}
          onClick={(education: string) => setValue('education', education)}
          textInputValue={otherLevelOfEducation}
          setTextInputValue={setOtherLevelOfEducation}
        />
      </VStack>
      <VStack gap={LABEL_INPUT_VERTICAL_GAP}>
        <Text variant={'urbanistBoldLarge'}>Choose your learning style preference</Text>
        <HStack flexWrap={'wrap'} gap={5}>
          {LEARNING_STYLES.map((learningStyle) => (
            <CheckBoxTile<{ label: string }>
              onClick={updateLearningStyles}
              isChecked={selectedLearningStylePreference?.includes(learningStyle)}
              key={learningStyle}
              checkboxOption={{ label: learningStyle }}
            />
          ))}
        </HStack>
      </VStack>
      <Button
        onClick={onFormSubmit}
        variant={'continue'}
        marginTop={'12px'}
        marginBottom={onSkip ? 0 : '48px'}
        isDisabled={!isFormValid || isLoading}
        disabled={!isFormValid || isLoading}
        isLoading={isLoading}
      >
        {submitLabel}
      </Button>
      {onSkip && (
        <Button variant="link" onClick={onSkip} isDisabled={isLoading} marginBottom="48px">
          Skip
        </Button>
      )}
    </VStack>
  );
};

export default DemographicsForm;
